import { inject } from '@angular/core';
import { CanMatchFn, ResolveFn, Routes } from '@angular/router';
import { catchError, forkJoin, map, of } from 'rxjs';
import { MainComponent } from './components/main/main.component';
import { WebviewService } from './services/platform/webview.service';
import { WaypointService } from './services/core/waypoint.service';
import { initResolver } from './resolvers/init.resolver';
import { googleSearchResolver } from './resolvers/google-search.resolver';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { DeleteAccountComponent } from './components/delete-account/delete-account.component';
import { InitErrorComponent } from './components/init-error/init-error.component';
import { WidgetDocumentationComponent, WIDGET_DOCUMENTATION_PATH } from './components/widget-documentation/widget-documentation.component';
import { WidgetErrorComponent, WIDGET_ERROR_PATH } from './components/widget-error/widget-error.component';
import { widgetApiKeyGuard } from './guards/widget-api-key.guard';
import { MapReadyService } from './services/map/map-ready.service';
import { AccountService } from './services/account/account.service';
import { WidgetService, WIDGET_PATH, isWidgetHost } from './services/platform/widget.service';

const accountResolver: ResolveFn<null> = () => {
  // The widget is anonymous-only: a login cookie left behind by the full app must
  // not turn the embedded map into the logged-in experience.
  if (inject(WidgetService).isWidget()) return of(null);
  const accountService = inject(AccountService);
  const authorized = accountService.initAccount();
  if (!authorized) return of(null);
  return forkJoin([
    accountService.loadUserProfile(),
    accountService.getUserSettings(),
  ]).pipe(
    map(() => null),
    catchError(() => of(null)),
  );
};

const mainResolver = {
  webviewService: () => inject(WebviewService).resolve(),
  waypointService: () => { inject(WaypointService).restore(); return true; },
  initResolver: initResolver,
  accountResolverService: accountResolver,
  googleSearchResolver: googleSearchResolver,
  mapReadyService: () => { inject(MapReadyService).resolve(); return true; },
};

// On the widget host (widget.<domain>) the root is the widget and the full-site pages do not
// match; their paths fall through to '**' and so land on the widget as well.
const onWidgetHost: CanMatchFn = () => isWidgetHost();
const offWidgetHost: CanMatchFn = () => !isWidgetHost();

export const routes: Routes = [
  // canActivate runs before the resolvers, so an unauthorised widget costs no init requests.
  { path: '', canMatch: [onWidgetHost], component: MainComponent, canActivate: [widgetApiKeyGuard], resolve: mainResolver },
  { path: '', component: MainComponent, resolve: mainResolver },
  { path: 'privacy-policy', canMatch: [offWidgetHost], component: MainComponent, resolve: mainResolver },
  { path: 'demo', canMatch: [offWidgetHost], component: MainComponent, resolve: mainResolver },
  { path: 'polyline', canMatch: [offWidgetHost], component: MainComponent, resolve: mainResolver },
  { path: 'verify-email', canMatch: [offWidgetHost], component: MainComponent, resolve: mainResolver },
  { path: 'terahive', canMatch: [offWidgetHost], component: MainComponent, resolve: mainResolver },
  // The widget runs only on the widget host. The former /widget path goes to the root, query kept:
  // on the widget host that is the widget, on the main site the full site.
  { path: WIDGET_PATH, redirectTo: '' },
  { path: WIDGET_ERROR_PATH, component: WidgetErrorComponent },
  // Developer documentation of the widget parameters — a standalone page, no map, no resolvers.
  // Widget host only; on the main site the path falls through to '**'.
  { path: WIDGET_DOCUMENTATION_PATH, canMatch: [onWidgetHost], component: WidgetDocumentationComponent },
  { path: 'reset-password', canMatch: [offWidgetHost], component: ResetPasswordComponent },
  { path: 'delete-confirmation', canMatch: [offWidgetHost], component: DeleteAccountComponent },
  { path: 'init-error', component: InitErrorComponent },
  { path: '**', redirectTo: '', pathMatch: 'full' }
];
